import React, { useEffect, useState, useMemo } from 'react';
import { Tour, Button, Space, Typography, Progress, Tooltip } from 'antd';
import {
  QuestionCircleOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * 進捗表示とスキップ機能付きの拡張ツアーコンポーネント
 */
const EnhancedTour = ({
  open,
  steps,
  onClose,
  onFinish,
  onSkip,
  current,
  onChange,
  allowSkip = true,
  tourTitle = 'ガイドツアー',
  ...restProps
}) => {
  const totalSteps = steps?.length || 0;
  const progressPercent = totalSteps > 0 ? Math.round(((current + 1) / totalSteps) * 100) : 0;
  
  // ターゲット要素の再評価用のカウンター
  const [targetRefreshKey, setTargetRefreshKey] = useState(0);

  // currentが変わったらターゲットを再評価
  useEffect(() => {
    if (open) {
      // 少し遅延を入れてDOMが更新されるのを待つ
      const timer = setTimeout(() => {
        setTargetRefreshKey(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [current, open]);

  // ステップごとにカスタムフッターを追加（targetを動的に評価）
  const enhancedSteps = useMemo(() => {
    return steps?.map((step, index) => ({
      ...step,
      // targetを動的に評価（関数の場合は呼び出す）
      target: typeof step.target === 'function' ? step.target() : step.target,
      // タイトルに進捗を追加
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{typeof step.title === 'string' ? step.title : step.title}</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
            {index + 1} / {totalSteps}
          </Text>
        </div>
      ),
      // 説明文の下に進捗バーを追加
      description: (
        <div>
          <div style={{ marginBottom: 12, whiteSpace: 'pre-line' }}>{step.description}</div>
          <Progress
            percent={Math.round(((index + 1) / totalSteps) * 100)}
            size="small"
            showInfo={false}
            strokeColor={{
              '0%': '#1890ff',
              '100%': '#52c41a',
            }}
            style={{ marginBottom: 8 }}
          />
        </div>
      ),
    }));
  }, [steps, totalSteps, targetRefreshKey]);

  // カスタムのprevButtonRender / nextButtonRender
  const prevButtonProps = {
    children: (
      <Space size={4}>
        <LeftOutlined />
        前へ
      </Space>
    ),
  };

  const nextButtonProps = (currentStep) => {
    const isLast = currentStep === totalSteps - 1;
    return {
      type: 'primary',
      children: (
        <Space size={4}>
          {isLast ? (
            <>
              完了
              <CheckOutlined />
            </>
          ) : (
            <>
              次へ
              <RightOutlined />
            </>
          )}
        </Space>
      ),
    };
  };

  // スキップボタンを含むカスタムフッター
  const renderIndicators = (currentStep, totalSteps) => {
    if (!allowSkip) return null;
    
    return (
      <Button
        type="text"
        size="small"
        onClick={onSkip}
        style={{ 
          color: '#8c8c8c',
          fontSize: 12,
          position: 'absolute',
          top: 8,
          right: 8,
        }}
      >
        あとで見る
      </Button>
    );
  };

  return (
    <Tour
      open={open}
      steps={enhancedSteps}
      onClose={onClose}
      onFinish={onFinish}
      current={current}
      onChange={onChange}
      indicatorsRender={renderIndicators}
      prevButtonProps={prevButtonProps}
      nextButtonProps={nextButtonProps(current)}
      {...restProps}
    />
  );
};

/**
 * ツアー再表示用のヘルプアイコンボタン
 */
export const TourHelpButton = ({
  onClick,
  tooltip = 'ガイドツアーを表示',
  style = {},
  size = 'default',
}) => {
  return (
    <Tooltip title={tooltip}>
      <Button
        type="text"
        icon={<QuestionCircleOutlined />}
        onClick={onClick}
        size={size}
        style={{
          color: '#1890ff',
          ...style,
        }}
      />
    </Tooltip>
  );
};

export default EnhancedTour;
